/**
 * Shared tenant-app contracts — copied verbatim from the backend interface file
 * (`metaship-backend/Services/TenantApps/tenant-apps.interface.ts`) per the design
 * spec's Appendix A1–A3 (docs/superpowers/specs/2026-08-27-tenant-apps-white-label-design.md).
 * Keep in lock-step with that file; this is the one place in `standalone/` allowed
 * to duplicate a backend type (Encore has no shared-package export for API types).
 *
 * `TenantBranding` / `TenantBrandingColours` are NOT redeclared here — they come
 * from `@tenant-theme`, which is the single source of truth every Vue3 module
 * (including this shell) overlays tenant colours from.
 */
import type { TenantBranding } from "@tenant-theme";

export type TenantAppKind = "WHITE_LABEL" | "PRODUCT";
export type TenantAppStatus =
  | "DRAFT"
  | "PROVISIONING"
  | "LIVE"
  | "FAILED"
  | "DISABLED"
  | "DEPROVISIONING"
  | "DELETED";

/** A1 */
export interface TenantAppModuleRef {
  systemModuleId: number;
  sortOrder: number;
  allowedItems: string[] | null;
}

/** A2 */
export interface ResolvedMfe {
  importKey: string;
  pathPrefix: string;
  matchMode: "prefix" | "exact";
  overlay: boolean;
  allowedItems: string[] | null;
}

export interface ImportMapEntry {
  key: string;
  url: string;
}

/**
 * A7 — configurable per-app home page. `route` is a module root or an
 * allowed sub-item route; `icon` is a Material Icons ligature name (same
 * convention as `NavBarItem`/`NavBarSubItem`).
 */
export interface TenantHomeTile {
  icon: string;
  title: string;
  description: string | null;
  route: string;
}

export interface TenantHomePage {
  enabled: boolean;
  welcomeTitle: string | null;
  welcomeText: string | null;
  tiles: TenantHomeTile[];
}

/**
 * A8 — the PRE-auth landing page (distinct from A7's post-auth App Home).
 * "default" renders the shell's built-in hero + sign-in card; "custom"
 * renders `htmlUrl` full-bleed in a sandboxed iframe. The backend degrades a
 * custom page with no document back to "default", so `mode === "custom"`
 * always implies a non-null `htmlUrl`.
 */
export type TenantLoginPageMode = "default" | "custom";

/**
 * Sign-in page options (2026-09-02) — the copy and links around the sign-in
 * button, for BOTH modes. Nullable throughout: null means "not shown". The
 * server normalises them (https-only URLs), but the shell re-checks on read
 * (`tenant/loginLinks.ts`) the same way the splash record is re-checked.
 */
export interface TenantLoginPageOptions {
  /** Button label (≤ 40 chars); null → "Sign in". */
  signInLabel: string | null;
  supportEmail: string | null;
  supportUrl: string | null;
  termsUrl: string | null;
  privacyUrl: string | null;
  /** Show the "Powered by Metaship" credit. */
  poweredBy: boolean;
  /**
   * Better Auth sign-in methods this app shows (plan Task 9). Optional: an
   * older backend omits it, and absent or empty means all four.
   */
  providers?: string[] | null;
  /**
   * The animated scene behind the sign-in page's left panel. "contours" (default, drifting
   * lines) or "network" (one record, every party: the Metaship landing-page hero). Optional:
   * an older backend omits it.
   */
  panelScene?: PanelScene | null;
  /** The "network" scene's party names around the hub (up to six). Absent → the default six. */
  panelNodes?: string[] | null;
}

export type PanelScene = "contours" | "network";

export interface TenantLoginPage extends TenantLoginPageOptions {
  mode: TenantLoginPageMode;
  htmlUrl: string | null;
  fileName: string | null;
  updatedAt: string | null;
}

/** The built-in page with no options set — what an older backend's resolve
 *  answer means, and the fixture every test starts from. */
export function defaultLoginPage(overrides: Partial<TenantLoginPage> = {}): TenantLoginPage {
  return {
    mode: "default",
    htmlUrl: null,
    fileName: null,
    updatedAt: null,
    signInLabel: null,
    supportEmail: null,
    supportUrl: null,
    termsUrl: null,
    privacyUrl: null,
    poweredBy: true,
    ...overrides,
  };
}

export interface ResolveTenantAppResponse {
  found: boolean;
  slug: string | null;
  kind: TenantAppKind | null;
  name: string | null;
  status: TenantAppStatus | null;
  homePath: string | null;
  /** null when App Home is disabled for this app. */
  homePage: TenantHomePage | null;
  organisation: { id: number; name: string } | null;
  branding: TenantBranding | null;
  modules: ResolvedMfe[];
  importMap: { imports: ImportMapEntry[] };
  /** Always present — "default" when the app has no custom landing page. */
  loginPage: TenantLoginPage;
  /**
   * DEV-937 — may a signed-in visitor with NO organisation create one here?
   * Decides whether an orgless arrival sees the sign-up form or "ask an
   * administrator". Optional so an older backend (which omits it) reads as
   * false rather than crashing the boot.
   */
  selfServeSignup?: boolean;
  /**
   * PAC-1374 — which sign-up form an orgless visitor gets when
   * `selfServeSignup` is on: "FARM" (Boer) or "COMPANY" (a WMS front door such
   * as packhouse.metaship.ai). Optional/null from an older backend → the farm
   * form, which is today's behaviour.
   */
  signupForm?: TenantSignupForm | null;
}

export type TenantSignupForm = "FARM" | "COMPANY";

/**
 * A3 — `GET /tenant-apps/my-access?slug=<tenant.slug>` (binding design
 * addendum, 2026-08-27: keyed by the resolved app's SLUG, not its hostname —
 * a slug is stable identity; a hostname is routing).
 */
export interface TenantAppMyAccessResponse {
  found: boolean;
  kind: TenantAppKind | null;
  organisationIds: number[];
}

/**
 * `/user/permission` — the NavBarList shape filtered to the caller's access
 * (`metaship-backend/Static/Navbar.ts`, `Services/UserAccess/user-access-controller.ts`).
 * Only the fields the shell reads are typed; the backend payload carries more.
 */
export interface NavBarItem {
  name: string;
  icon?: string;
  route: string;
  group?: boolean;
  subtitle?: string;
  autoDrill?: boolean;
  items?: NavBarSubItem[];
}

export interface NavBarSubItem {
  name: string;
  icon?: string;
  route: string;
  section?: string | null;
  requiresTagId?: number | number[];
}

/**
 * `metaship-backend/Services/User/user.ts:getUserPermissions` returns
 * `{ data: NavBarItem[] }` — NOT `{ result }`. The shell read `result` and so
 * filtered an `undefined` list on every single sign-in: the sidebar rendered
 * "Home" and nothing else for every tenant app, regardless of the app's
 * modules. `result` stays declared (optional) because the neighbouring
 * UserAccess endpoints DO use it, and one payload shape per endpoint is not
 * worth a second helper.
 */
export interface UserPermissionResponse {
  message?: string;
  data?: NavBarItem[];
  result?: NavBarItem[];
}

/** `GET /user-access/organisation` — one row per org the user belongs to. */
export interface UserOrganisation {
  id: number;
  name: string;
  /** Org-scoped bearer minted at login; the payload sent to `/user-access/refresh-token`. */
  token: string;
  accountStatus: string | null;
  icon?: string | null;
  logo?: string | null;
}

export interface GetUserOrganisationsResponse {
  result: UserOrganisation[];
}

/**
 * `POST /user-access/refresh-token` — `tenantAppSlug` is REQUIRED on every
 * call from this shell (initial sign-in, org switch, proactive refresh); the
 * backend enforces the tenant-app access gate on this endpoint (not just on
 * `my-access`) and returns 403 when the org isn't part of the app (binding
 * design addendum, 2026-08-27) — see `TenantAccessDeniedError` in `auth/session.ts`.
 */
export interface RefreshTokenRequest {
  organisation: string;
  organisationName: string;
  tenantAppSlug: string;
}

export interface RefreshTokenResponse {
  token: string;
  accountStatus?: string | null;
  displayWarning?: boolean;
  suspensionDate?: string | null;
  refreshed?: boolean;
  requiresLogout?: boolean;
}
